import { Editor, loader, Monaco } from "@monaco-editor/react";
import { getEditorTheme, loadMonaco } from "components/Form/components/CodeEditor/monacoSetup";
import type { editor } from "monaco-editor";
import { useEffect, useRef, useState } from "react";

const MIN_HEIGHT = 90;
const MAX_HEIGHT = 400;

type MarkdownEditorProps = {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    autoFocus?: boolean;
    readOnly?: boolean;
};

/**
 * The plain-text ("edit") view of a MARKDOWN form field, rendered with the same
 * Monaco editor the Console uses everywhere else (flow/script/text fields) —
 * configured for the `markdown` language so headings, emphasis, links, code
 * fences etc. are syntax-highlighted while typing.
 *
 * It reuses the shared `monacoSetup` bootstrap (loadMonaco / getEditorTheme) so
 * it participates in the same coordinated Monaco load profile as CodeEditor,
 * rather than pulling in a second Monaco instance. Markdown fields do not need
 * breakpoints, linting or the current-line indicator, so this is a thin editor
 * without CodeEditor's execution-oriented machinery.
 *
 * Height auto-grows with the content (clamped between MIN_HEIGHT and
 * MAX_HEIGHT), mirroring the auto-sizing textarea it replaces; beyond the
 * maximum the editor scrolls internally.
 */
export const MarkdownEditor = ({
    value,
    onChange,
    onBlur,
    autoFocus,
    readOnly,
}: MarkdownEditorProps) => {
    const [editorReady, setEditorReady] = useState(false);
    const [height, setHeight] = useState(MIN_HEIGHT);
    const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
    const language = "markdown";

    useEffect(() => {
        let cancelled = false;
        setEditorReady(false);

        const initialize = async () => {
            try {
                const monaco: Monaco = await loadMonaco(language);
                loader.config({ monaco });
                if (!cancelled) {
                    setEditorReady(true);
                }
            } catch (error) {
                console.error("Failed to initialize markdown editor", error);
            }
        };

        void initialize();

        return () => {
            cancelled = true;
        };
    }, []);

    const syncHeight = (editorInstance: editor.IStandaloneCodeEditor) => {
        const contentHeight = editorInstance.getContentHeight();
        setHeight(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, contentHeight)));
    };

    if (!editorReady) {
        return null;
    }

    return (
        <Editor
            height={height}
            language={language}
            theme={getEditorTheme(language)}
            value={value}
            options={{
                minimap: { enabled: false },
                readOnly: readOnly ?? false,
                // Prose-oriented: wrap long lines, drop the line-number/glyph
                // gutters so the editor reads like an enriched text field and
                // fills the full available width.
                wordWrap: "on",
                lineNumbers: "off",
                glyphMargin: false,
                folding: false,
                lineDecorationsWidth: 0,
                lineNumbersMinChars: 0,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                cursorBlinking: "smooth",
                overviewRulerLanes: 0,
                renderLineHighlight: "none",
                scrollbar: {
                    alwaysConsumeMouseWheel: false,
                },
            }}
            onChange={(next) => onChange(next ?? "")}
            onMount={(editorInstance) => {
                editorRef.current = editorInstance;
                syncHeight(editorInstance);
                editorInstance.onDidContentSizeChange(() => syncHeight(editorInstance));
                if (onBlur) {
                    editorInstance.onDidBlurEditorText(() => onBlur());
                }
                if (autoFocus) {
                    editorInstance.focus();
                }
            }}
        />
    );
};
